import React, { useEffect, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { Box, Typography, IconButton } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styles from './CallDrawerModern.module.scss'

export const ConversationModal = ({ open, onClose, conversationFlow, callId, agentName, callerName }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, handleKeyDown])

  const mergedSegments = useMemo(() => {
    if (!conversationFlow || conversationFlow.length === 0) return []

    const merged = []
    let currentSegment = null

    conversationFlow.forEach((segment) => {
      if (currentSegment && currentSegment.speaker === segment.speaker) {
        currentSegment.content += ' ' + segment.content
      } else {
        if (currentSegment) {
          merged.push(currentSegment)
        }
        currentSegment = { ...segment }
      }
    })

    if (currentSegment) {
      merged.push(currentSegment)
    }

    return merged
  }, [conversationFlow])

  if (!open) return null

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return ReactDOM.createPortal(
    <Box
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="conversation-modal-title"
    >
      <Box className={styles.transcriptModal}>
        <Box className={styles.transcriptModalHeader}>
          <Box className={styles.transcriptModalHeaderLeft}>
            <Box className={styles.transcriptModalIcon}>
              <FontAwesomeIcon icon="comments" />
            </Box>
            <Box>
              <Typography className={styles.transcriptModalTitle}>
                Full Transcript
              </Typography>
              <Typography className={styles.transcriptModalSubtitle}>
                {callId && `Call #${callId}`} {mergedSegments.length > 0 && `• ${mergedSegments.length} messages`}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            className={styles.transcriptModalClose}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon="times" />
          </IconButton>
        </Box>

        <Box className={styles.transcriptModalBody}>
          {mergedSegments.length > 0 ? (
            <Box className={styles.chatContainer}>
              {mergedSegments.map((segment, index) => {
                const isAgent = segment.speaker === 'agent'
                return (
                  <Box
                    key={`${callId}-${index}`}
                    className={`${styles.chatMessage} ${isAgent ? styles.chatMessageAgent : styles.chatMessageCaller}`}
                  >
                    <Box className={`${styles.chatAvatar} ${isAgent ? styles.chatAvatarAgent : styles.chatAvatarCaller}`}>
                      {isAgent ? (
                        <img
                          src="/favicon.ico"
                          alt="Agent"
                          className={styles.chatAvatarImg}
                        />
                      ) : (
                        <FontAwesomeIcon icon="user" />
                      )}
                    </Box>
                    <Box className={styles.chatBubbleWrapper}>
                      <Typography className={styles.chatSpeakerName}>
                        {isAgent ? (agentName || 'Agent') : (callerName || 'Caller')}
                        {segment.timestamp && (
                          <span className={styles.chatTimestamp}>{segment.timestamp}</span>
                        )}
                      </Typography>
                      <Box className={`${styles.chatBubble} ${isAgent ? styles.chatBubbleAgent : styles.chatBubbleCaller}`}>
                        <Typography className={styles.chatBubbleText}>
                          {segment.content}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          ) : (
            <Box className={styles.transcriptEmpty}>
              <FontAwesomeIcon icon="comments" className={styles.transcriptEmptyIcon} />
              <Typography className={styles.transcriptEmptyTitle}>No Transcript Available</Typography>
              <Typography className={styles.transcriptEmptyText}>
                The conversation transcript is not available for this call.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>,
    document.body
  )
}

ConversationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conversationFlow: PropTypes.arrayOf(PropTypes.shape({
    speaker: PropTypes.string,
    content: PropTypes.string,
    timestamp: PropTypes.string
  })),
  callId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  agentName: PropTypes.string,
  callerName: PropTypes.string
}

export default ConversationModal
